import { useState } from 'react';
import { LAYOUTS } from '../utils/theme';
import { inputClass } from './AuthLayout';

/** Building blocks shared by the card editor and Company → Brand. */

export function Section({ title, hint, children }) {
  return (
    <section className="bg-white rounded-2xl border border-line p-5 sm:p-6">
      <h2 className="text-xl font-bold text-ink">{title}</h2>
      {hint && <p className="text-sm text-ink-muted mt-1">{hint}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

export function LockNote() {
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-ink-muted">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" />
      </svg>
      Set by your company
    </span>
  );
}

export function Toggle({ checked, onChange, disabled, label }) {
  return (
    <label className="flex items-center justify-between gap-4 cursor-pointer">
      <span className="text-sm font-semibold text-ink">{label}</span>
      <button type="button" role="switch" aria-checked={checked} disabled={disabled} onClick={() => onChange(!checked)}
        className={`relative w-12 h-7 rounded-full shrink-0 transition ${checked ? 'bg-blue' : 'bg-line-strong'} disabled:opacity-50`}>
        <span className={`absolute top-[3px] w-[22px] h-[22px] rounded-full bg-white transition-all ${checked ? 'left-[23px]' : 'left-[3px]'}`} />
      </button>
    </label>
  );
}

/** Five layout thumbnails, drawn in the company's colors. */
export function LayoutPicker({ value, onChange, disabled, t }) {
  return (
    <div role="radiogroup" aria-label="Card layout" className="grid grid-cols-2 sm:grid-cols-5 gap-3">
      {LAYOUTS.map((l) => {
        const on = value === l.id;
        return (
          <button type="button" role="radio" aria-checked={on} key={l.id} disabled={disabled} onClick={() => onChange(l.id)}
            title={l.description}
            className={`p-2.5 rounded-xl flex flex-col gap-2 text-left text-[13px] font-semibold text-ink transition disabled:opacity-60 disabled:cursor-not-allowed
              ${on ? 'border-2 border-blue bg-blue-soft' : 'border border-line bg-white hover:border-line-strong'}`}>
            <Thumb id={l.id} t={t} />
            {l.name}
          </button>
        );
      })}
    </div>
  );
}

function Thumb({ id, t }) {
  const p = t.primary_color, a = t.accent_color;
  const base = 'h-[92px] rounded-lg overflow-hidden flex flex-col';
  if (id === 'classic') return (
    <span className={`${base} bg-white border border-line`}>
      <span className="h-[30px]" style={{ background: p }} />
      <span className="w-6 h-6 rounded-full -mt-3 ml-2.5 border-2 border-white" style={{ background: t.accentSoft }} />
      <span className="h-1.5 w-1/2 mt-2 mx-2.5 rounded bg-ink" />
      <span className="h-3 mt-2.5 mx-2.5 rounded" style={{ background: a }} />
    </span>
  );
  if (id === 'banner') return (
    <span className={`${base} bg-white border border-line items-center`}>
      <span className="h-[38px] w-full" style={{ background: p }} />
      <span className="w-7 h-7 rounded-full -mt-3.5 border-2 border-white" style={{ background: t.accentSoft }} />
      <span className="h-1.5 w-1/2 mt-1.5 rounded bg-ink" />
      <span className="h-2.5 w-3/4 mt-2 rounded-full" style={{ background: a }} />
    </span>
  );
  if (id === 'split') return (
    <span className={`${base} bg-surface p-2 grid grid-cols-2 gap-1.5`} style={{ display: 'grid' }}>
      <span className="col-span-2 bg-white rounded flex items-center gap-1 p-1"><span className="w-4 h-5 rounded" style={{ background: t.accentSoft }} /><span className="h-1.5 w-8 rounded bg-ink" /></span>
      <span className="bg-white rounded" /><span className="bg-white rounded" />
    </span>
  );
  if (id === 'minimal') return (
    <span className={`${base} bg-white border border-line p-2.5 gap-1.5`}>
      <span className="h-2.5 w-[70%] rounded-sm bg-ink" /><span className="h-2.5 w-[55%] rounded-sm bg-ink" />
      <span className="h-px bg-ink mt-2" /><span className="h-px bg-line mt-1.5" /><span className="h-px bg-line mt-1.5" />
    </span>
  );
  return (
    <span className={`${base} p-2.5 gap-1.5 relative`} style={{ background: p }}>
      <span className="absolute -right-2.5 top-3.5 w-10 h-10 rounded-full border-[3px] border-coral opacity-60" />
      <span className="h-3 w-[60%] rounded-sm mt-6" style={{ background: t.onPrimary }} />
      <span className="h-3 w-[45%] rounded-sm" style={{ background: t.onPrimary }} />
    </span>
  );
}

export function ColorField({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-ink mb-1.5">{label}</span>
      <span className="flex items-center gap-2">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} className="h-11 w-12 rounded-lg border border-line-strong p-1 bg-white" />
        <input value={value} onChange={(e) => /^#[0-9A-Fa-f]{0,6}$/.test(e.target.value) && onChange(e.target.value.toUpperCase())}
          className={`${inputClass} font-mono`} maxLength={7} aria-label={`${label} hex value`} />
      </span>
    </label>
  );
}

export function ImagePicker({ preview, fallback, t, onPick, onRemove, disabled, round = false, label, help, lockedNote, allowSvg = false }) {
  const types = ['image/jpeg', 'image/png', 'image/webp', ...(allowSvg ? ['image/svg+xml'] : [])];
  const [error, setError] = useState('');
  const pick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    setError('');
    if (!file) return;
    if (!types.includes(file.type)) return setError(allowSvg ? 'Use an SVG, PNG, JPEG or WebP image.' : 'Use a JPEG, PNG or WebP image.');
    if (file.size > 8 * 1024 * 1024) return setError('That image is over 8 MB.');
    onPick(file);
  };
  const initialsText = (fallback || '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');
  return (
    <div className="flex flex-wrap items-center gap-4">
      <div className={`${round ? 'w-20 h-20 rounded-full' : 'w-32 h-16 rounded-lg'} border border-line overflow-hidden flex items-center justify-center shrink-0`}
        style={{ background: round && t ? t.accentSoft : '#F4F7FA' }}>
        {preview ? <img src={preview} alt="" className={`w-full h-full ${round ? 'object-cover' : 'object-contain p-1'}`} />
          : round && initialsText ? <span className="font-display font-extrabold text-2xl" style={{ color: t?.accent_color }}>{initialsText}</span>
          : <span className="text-xs text-ink-muted">None</span>}
      </div>
      <div className="space-y-1.5">
        <div className="flex flex-wrap items-center gap-3">
          <label className={`inline-flex items-center h-10 px-4 rounded-lg border border-line-strong bg-white text-sm font-semibold text-ink ${disabled ? 'opacity-50' : 'cursor-pointer hover:bg-surface'}`}>
            {label}
            <input type="file" accept={types.join(',')} className="sr-only" onChange={pick} disabled={disabled} />
          </label>
          {preview && onRemove && !disabled && (
            <button type="button" onClick={onRemove} className="text-sm font-medium text-ink-muted hover:text-coral-ink">Remove</button>
          )}
        </div>
        {lockedNote ? <p className="text-sm text-ink-muted">{lockedNote}</p> : help && <p className="text-sm text-ink-muted">{help}</p>}
        {error && <p className="text-sm text-coral-ink">{error}</p>}
      </div>
    </div>
  );
}
